import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { AdminShell } from "@/components/admin/admin-shell";
import { Pill } from "@/components/admin/status-badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useSetCustomerStatus, useShipments, useUsersByRole } from "@/lib/queries";
import { formatDate, formatPhone } from "@/lib/format";
import { toast } from "sonner";

export const Route = createFileRoute("/customers")({
  component: CustomersPage,
});

function CustomersPage() {
  const { data: customers, isLoading } = useUsersByRole("customer");
  const { data: shipments } = useShipments();
  const setStatus = useSetCustomerStatus();

  // Set page title on mount
  useEffect(() => {
    document.title = "Customers — 24Boda Admin";
  }, []);

  const counts = new Map<string, number>();
  for (const s of shipments ?? []) {
    counts.set(s.customerId, (counts.get(s.customerId) ?? 0) + 1);
  }

  return (
    <AdminShell title="Customers" description="All registered customers">
      <Card className="overflow-hidden rounded-lg p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Shipments</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                  Loading customers…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && (customers?.length ?? 0) === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                  No customers yet.
                </TableCell>
              </TableRow>
            )}
            {(customers ?? []).map((c) => (
              <TableRow key={c.uid}>
                <TableCell className="font-medium">{c.displayName || "Unnamed"}</TableCell>
                <TableCell>{formatPhone(c.phoneE164)}</TableCell>
                <TableCell>{counts.get(c.uid) ?? 0}</TableCell>
                <TableCell>{formatDate(c.createdAt)}</TableCell>
                <TableCell>
                  <Pill tone={c.status === "active" ? "success" : "neutral"}>
                    {c.status.replace("_", " ")}
                  </Pill>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setStatus.mutate(
                        { uid: c.uid, status: c.status === "active" ? "suspended" : "active" },
                        {
                          onSuccess: () =>
                            toast.success(c.status === "active" ? "Account suspended" : "Account activated"),
                          onError: () => toast.error("Could not update account"),
                        },
                      )
                    }
                  >
                    {c.status === "active" ? "Suspend" : "Activate"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </AdminShell>
  );
}
